import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import imgContainerWorkshop from '../../assets/news-events/container_workshop/Capa.png';
import imgContainerWorkshop1 from '../../assets/news-events/container_workshop/Imagem 1.png';
import imgContainerWorkshop2 from '../../assets/news-events/container_workshop/Imagem 2.png';
import imgContainerWorkshop3 from '../../assets/news-events/container_workshop/Imagem 3.png';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';

const ContainerWorkshop = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on component mount
    }, []);

    const { t } = useTranslation();
    const ogTitle = t('Hydraulic Container Workshop');
    const ogDescription = t('Reduce machine downtime with the HANSA-FLEX Container Workshop. Complete hydraulic solutions and on-site hose fabrication at your construction site or industrial facility with certified quality.');
    const ogImage = `https://www.hansa-flex.com.br/${imgContainerWorkshop}`;
    const ogUrl = `https://www.hansa-flex.com.br/container-workshop`;

    return (
        <div>
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgContainerWorkshop}
                imageAlt="Complete infrastructure anywhere: HANSA-FLEX Container Workshop"
                meta="April 22, 2026 | Gabriela Nascimento"
                title={"Complete infrastructure anywhere: HANSA-FLEX Container Workshop"}
                text={"A hydraulic hose bursts at a remote construction site or on an offshore platform. The schedule is tight, the cost of downtime is sky-high, and the nearest supplier is miles away. Does this scenario sound familiar? If your operation can’t wait, the solution isn’t to find a workshop—it’s to bring the workshop to your machine. Learn how to transform your maintenance logistics."}
                readingTimeMinutes={10}
            />

            <MagazineTextBlock title="The Hidden Cost of Waiting in Hydraulic Maintenance"
                paragraphs={[
                    "In the industrial sector, especially in areas such as mining, construction, and offshore, time is literally money. When a hydraulic system fails, waiting for a replacement part can take hours or even days, resulting in substantial financial losses.",
                    "HANSA-FLEX understands that operational continuity is your biggest challenge. That’s why we developed Container Workshops, a strategic solution that eliminates the wait for suppliers and places hose manufacturing at the heart of your operation, available 24 hours a day, 7 days a week.",
                ]}
            />

            <MagazineTextBlock 
            imageSrc={imgContainerWorkshop1}
            imageAlt="High-Performance Infrastructure: What’s Inside the Container?"
            title="High-Performance Infrastructure: What’s Inside the Container?"
                paragraphs={[
                    "It’s not just a storage space, but a fully equipped, customized workshop. The HANSA-FLEX Container Workshop is designed to offer mobility and immediate access to spare parts, regardless of weather or geographical conditions.",
                    "Models adaptable to your needs:",
                ]}
                bulletPoints={[
                    {
                        // point: "",
                        paragraphs: ["HSC20: Single 20-foot container, fully equipped for the manufacture of assembled hoses tailored to your project’s requirements.",
                            "Technical specifications: DIN 853 / 857 - 1SN / 1SC - up to 2“ / DN50 / size 32, DIN 853 / 857 - 2SN / 2SC - up to 2” / DN50 / size 32, SAE100R15 - up to 1.1/4” / DN32 / size 20.",
                            "Total length of assembled hoses: 1/4” to 5/8” - 10 m, 3/4” to 1” - 8 m, 1.1/4” to 2” - 3 m."]
                    },
                    {
                        // point: "",
                        paragraphs: ["HSC40: 40-foot unit, offering more internal space for storage and operation.",
                            "Technical specifications: DIN 853 / 857 - 1SN / 1SC - up to 2“ / DN50 / size 32, DIN 853 / 857 - 2SN / 2SC - up to 2” / DN50 / size 32, SAE100R15 - up to 1.1/4” / DN32 / size 20.",
                            "Total length of assembled hoses: 1/4” to 5/8” - 15 m, 3/4” to 1” - 10 m, 1.1/4” to 2” - 5 m."]
                    },
                    {
                        // point: "",
                        paragraphs: ["Customized: Tailor-made designs with thermal insulation, air conditioning, and high-performance machinery according to your project’s needs."]
                    }
                ]}
                paragraphs2={[
                    "State-of-the-Art Equipment",
                    "Each unit leaves the factory ready for use, featuring:",
                ]}
                bulletPoints2={[
                    "High-precision crimping and cutting machines.",
                    "Hose cleaning and stripping system.",
                    "Air compressor and ergonomic workbenches.",
                    "Storage racks for smart inventory management."]
                }
            />

            <MagazineTextBlock 
            imageSrc={imgContainerWorkshop2}
            imageAlt="More Than Just Parts: On-Site Technical Expertise"
            title="More Than Just Parts: On-Site Technical Expertise"
                paragraphs={[
                    "A unique feature of our mobile workshop is its seamless integration with HANSA-FLEX’s industrial services. We don’t just deliver the equipment; we also provide training and consulting.",
                    "Our specialists can conduct intensive on-site sessions, training your team to assemble hydraulic hoses in accordance with HANSA-FLEX’s internal standards. This ensures that every hose produced inside the container bears the same quality seal you would find at any of our facilities worldwide. Furthermore, support continues throughout the project, with reliable material supply and optimized inventory replenishment.",
                ]}
            />

            <MagazineTextBlock title="Proven efficiency in extreme conditions"
                paragraphs={[
                    "Whether in a region of intense heat or extreme humidity, the insulation and robustness of our containers ensure the preservation of materials and a comfortable working environment.",
                    "You no longer have to accept the cost of machine downtime. With the ability to create custom hoses on the spot, you take full control of your maintenance. In large-scale operations, having your own workshop is the key to maintaining workflow and protecting your profits against unexpected failures.",
                ]}
            />

            <MagazineTextBlock 
            imageSrc={imgContainerWorkshop3}
            imageAlt="The Path to Uninterrupted Operations"
            title="The Path to Uninterrupted Operations"
                paragraphs={[
                    "Investing in a container workshop means prioritizing the safety and continuity of your business. By eliminating logistical dependencies and empowering your local workforce, you gain the agility to resolve hydraulic issues in minutes, not days. HANSA-FLEX is ready to be the right-hand man of your maintenance engineering, bringing technology and efficiency to the most challenging locations in Brazil.",
                    "Want to eliminate downtime in your operation?", 
                    "Request a quote for installing the Container Workshop at your company!",
                ]}
                buttonName="Contact us"
                buttonLink="/contact-us"
        />
        
        <div style={{ backgroundColor: '#f8f8f8' }}>
            <br />
            <br />
            <br />
        </div>    

        </div>
    )
}

export default ContainerWorkshop